/*
 * File: StoryContent.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on May 19, 2025 at 1:17 PM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 5, 2025 at 8:52 AM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

// Utilities
import { formatStoryText } from '../../../../utilities/story';
import { generateLongFormContentPlaceholder, generateShortFormContentPlaceholder } from '../../../utilities/placeholder';

// Components
import { Typography } from '../../Typography';
import { ObfuscatedText } from '../../ObfuscatedText';
import { Button } from '../../Button';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../../constants/base.constants';

// Styles
import * as S from './StoryContent.styles';


/**
 * Component
 */

export const StoryContent = ({
	story, isLoading
}) => {

	// Create state handlers
	const [isMediaObfuscated, setIsMediaObfuscated] = useState(story?.is_triggering);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Build title object
	const title = useMemo(() => (story ? `${!story?.title ? `#${story?.number}` : story.title}` : ''), [story]);

	// Generate placeholders
	const healingPlaceholder = useMemo(() => generateShortFormContentPlaceholder(), []);
	const hopePlaceholder = useMemo(() => generateShortFormContentPlaceholder(), []);
	const captionPlaceholder = useMemo(() => generateShortFormContentPlaceholder(), []);
	const storyPlaceholder = useMemo(() => generateLongFormContentPlaceholder(), []);

	// Handle uncensor image
	const uncensorImage = (e) => {

		// Prevent default
		e.preventDefault();

		// Update state
		setIsMediaObfuscated(false);
	};

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<S.StoryContainer>

			{/* Trigger Warning */}
			{story?.is_triggering && story.triggers.length > 0 && (
				<S.TriggerWarning>
					<Typography tag="p" variation="3">
						<Trans t={t} i18nKey={`triggerWarnings.${story.triggers[0]}_story`}>
							Dear reader, the following story contains language of self-harm that some may find triggering or discomforting.
						</Trans>
					</Typography>
				</S.TriggerWarning>
			)}

			{/* Message of Hope */}
			{story?.hope_message && (
				<S.ContentSection>
					<S.MessageCard>
						<Typography tag="h5" weight="bold">
							<Trans t={t} i18nKey="storyCard.messageOfHope">
								Message to a Survivor
							</Trans>
						</Typography>
						<Typography tag="p" variation="2" className={isLoading && 'shimmer shimmer-element'}>
							<ObfuscatedText>
								{formatStoryText(story?.hope_message) || hopePlaceholder}
							</ObfuscatedText>
						</Typography>
					</S.MessageCard>
				</S.ContentSection>
			)}

			{/* Message of Healing */}
			{story?.healing_message && (
				<S.ContentSection>
					<S.MessageCard>
						<Typography tag="h5" weight="bold">
							<Trans t={t} i18nKey="storyCard.messageOfHealing">
								Message of Healing
							</Trans>
						</Typography>
						<Typography tag="p" variation="2" className={isLoading && 'shimmer shimmer-element'}>
							<ObfuscatedText>
								{formatStoryText(story?.healing_message) || healingPlaceholder}
							</ObfuscatedText>
						</Typography>
					</S.MessageCard>
				</S.ContentSection>
			)}

			{/* Media */}
			{story?.media && (
				<S.ContentSection>
					<S.MediaContainer className={isMediaObfuscated && 'trigger'}>
						{isMediaObfuscated && (
							<S.TriggerToggleContainer>
								<S.LightenOverlay />
								<Button variation="white" onClick={uncensorImage}>
									<Trans t={t} i18nKey="storyCard.showArtworkAction">
										Show Artwork
									</Trans>
								</Button>
							</S.TriggerToggleContainer>
						)}
						<S.Overlay />
						<S.MediaImage src={story?.media} title={title} alt={title} />
					</S.MediaContainer>
				</S.ContentSection>
			)}

			{/* Artwork Artist */}
			{story?.artwork_artist && (
				<S.ArtworkArtist className={isLoading && 'shimmer shimmer-element'}>
					<ObfuscatedText>
						{formatStoryText(story?.artwork_artist)}
					</ObfuscatedText>
				</S.ArtworkArtist>
			)}

			{/* Story */}
			{(story?.story || isLoading) && (
				<S.ContentSection>
					<Typography tag="p" variation="2" className={isLoading && 'shimmer shimmer-element'}>
						<ObfuscatedText>
							{formatStoryText(story?.story) || storyPlaceholder}
						</ObfuscatedText>
					</Typography>
				</S.ContentSection>
			)}

			{/* Artwork Caption */}
			{story?.artwork_caption && (
				<S.ContentSection>
					<S.ContentSection>
						<Typography tag="p" variation="2" className={isLoading && 'shimmer shimmer-element'}>
							<ObfuscatedText>
								{formatStoryText(story?.artwork_caption) || captionPlaceholder}
							</ObfuscatedText>
						</Typography>
					</S.ContentSection>
				</S.ContentSection>
			)}

		</S.StoryContainer>
	);
};


/**
 * Configuration
 */

StoryContent.displayName = 'StoryContent';
StoryContent.propTypes = {
	story: PropTypes.shape(),
	isLoading: PropTypes.bool
};
StoryContent.defaultProps = {
	story: null,
	isLoading: false
};
